import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { useLoading } from '@pok/weblib/context/loading-context.jsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LinkedinIcon from '../../assets/images/share/linkedin.png';
import PublicIcon from '../../assets/images/share/public-post.png';
import RestrictedIcon from '../../assets/images/share/restricted-post.png';
import { createLinkedinShare, loadOrganization } from '../../interact.jsx';
import { buildLinkedinShareComment, buildShareTitle } from '../../utils/shareUtils.js';
import CloseIcon from '../icons/CloseIcon';

export default function LinkedinShareDialog({ data, open, onClose, certificate, image }) {
    const { t } = useTranslation();
    const [postVisibility, setPostVisibility] = useState('PUBLIC');
    const [commentary, setCommentary] = useState('');
    const [sharePostTitle, setSharePostTitle] = useState('');
    const { showLoading } = useLoading();
    const [img, setImg] = useState(image);

    useEffect(() => {
        setImg(image);
        if (open)
            loadOrganization(certificate.minter).then((x) => {
                setCommentary(buildLinkedinShareComment(certificate, x, t));
                setSharePostTitle(buildShareTitle(certificate, x, t));
            });
    }, [open]);

    const postVisibilities = [
        {
            value: 'PUBLIC',
            icon: PublicIcon,
        },
        {
            value: 'CONNECTIONS',
            icon: RestrictedIcon,
        },
    ];

    const _onClose = () => {
        onClose();
    };

    const onChangeVisibility = (e) => {
        setPostVisibility(e.target.value);
    };

    const onChangeCommentary = (e) => {
        setCommentary(e.currentTarget.value);
    };

    const onClickPost = (e) => {
        showLoading();
        createLinkedinShare(data.accessToken, certificate, sharePostTitle, commentary, postVisibility).then(
            (result) => {
                window.open('https://www.linkedin.com/feed/update/'.concat(result.shareUrn), '_blank');
                window.open(new URL(window.location.href).origin.concat('/c/').concat(certificate.id), '_self');
            },
        );
    };

    const domain = new URL(certificate.url).hostname;
    return (
        <>
            <Dialog
                open={open}
                onClose={_onClose}
                sx={{
                    '& .MuiPaper-root': {
                        padding: 4,
                        paddingBottom: 0,
                        borderRadius: '16px',
                        maxWidth: '500px',
                    },
                }}
            >
                <DialogTitle>
                    <Typography component="span" variant="h3">
                        {t('certificate.share.linkedin.post.label')}
                    </Typography>
                    <Divider style={{ marginTop: 12 }} />
                    <a onClick={_onClose} style={{ position: 'absolute', right: 12, top: 12, cursor: 'pointer' }}>
                        <CloseIcon />
                    </a>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="span">
                        <Stack direction={'row'} spacing={2}>
                            <Stack spacing={2} alignSelf={'center'}>
                                <SvgIcon style={{ width: '48px', height: '48px', borderRadius: '50%' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" id="person-accent-4">
                                        <path fill="#e7e2dc" d="M0 0h128v128H0z" />
                                        <path
                                            d="M88.41 84.67a32 32 0 10-48.82 0 66.13 66.13 0 0148.82 0z"
                                            fill="#788fa5"
                                        />
                                        <path
                                            d="M88.41 84.67a32 32 0 01-48.82 0A66.79 66.79 0 000 128h128a66.79 66.79 0 00-39.59-43.33z"
                                            fill="#9db3c8"
                                        />
                                        <path
                                            d="M64 96a31.93 31.93 0 0024.41-11.33 66.13 66.13 0 00-48.82 0A31.93 31.93 0 0064 96z"
                                            fill="#56687a"
                                        />
                                    </svg>
                                </SvgIcon>
                            </Stack>
                            <Stack spacing={0}>
                                <Box sx={{ fontWeight: '700', fontSize: '14px', color: 'black' }}>{data?.name}</Box>
                                <Select
                                    disableUnderline={true}
                                    variant="standard"
                                    sx={{
                                        minWidth: 140,
                                        '& .MuiInput-input:focus': {
                                            backgroundColor: 'transparent',
                                        },
                                        border: 3,
                                        paddingLeft: 1,
                                        paddingBottom: 2,
                                        paddingTop: 1,
                                        borderRadius: 10,
                                        minHeight: '28px',
                                        height: '28px',
                                    }}
                                    id="visibility-select"
                                    value={postVisibility}
                                    onChange={onChangeVisibility}
                                >
                                    {postVisibilities.map((p) => (
                                        <MenuItem key={p.value} value={p.value}>
                                            <Stack direction={'row'}>
                                                <Stack>
                                                    <img
                                                        src={p.icon}
                                                        style={{ width: '22px', height: '22px' }}
                                                        alt="linkedin icon"
                                                    />
                                                </Stack>
                                                <Stack
                                                    paddingLeft={1}
                                                    sx={{
                                                        fontWeight: '500',
                                                        fontSize: '12px',
                                                        height: '15px',
                                                    }}
                                                >
                                                    {t(
                                                        'certificate.share.linkedin.post.visibility.'.concat(
                                                            p.value.toLowerCase(),
                                                        ),
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <InputBase
                                onChange={onChangeCommentary}
                                multiline
                                minRows={2}
                                value={commentary}
                                autoFocus={true}
                                sx={{ ml: 1, flex: 1, paddingTop: 2, paddingBottom: 2 }}
                                placeholder={t('certificate.share.linkedin.post.placeholder')}
                                inputProps={{ 'aria-label': '' }}
                            />
                        </Stack>
                        <Stack
                            direction={'row'}
                            justifyContent={'center'}
                            sx={{
                                borderTopRightRadius: '8px',
                                borderTopLeftRadius: '8px',
                                padding: 0,
                                backgroundColor: '#e7e2dc',
                            }}
                            className="no-margin-stack"
                        >
                            <Stack
                                sx={{
                                    width: { xs: '100%' },
                                    height: { xs: '100%' },
                                }}
                                className="no-margin-stack"
                            >
                                <img
                                    src={img}
                                    style={{
                                        borderTopLeftRadius: '8px',
                                        borderTopRightRadius: '8px',
                                    }}
                                />
                            </Stack>
                        </Stack>
                        <Stack
                            sx={{
                                width: '100%',
                                backgroundColor: '#3A434E',
                                borderBottomLeftRadius: '8px',
                                borderBottomRightRadius: '8px',
                            }}
                            className="no-margin-stack"
                        >
                            <Typography component="span" variant="shareTitle">
                                <Box padding={2} paddingBottom={0}>
                                    {sharePostTitle}
                                </Box>
                            </Typography>
                            <Typography
                                component="span"
                                variant="shareLink"
                                style={{
                                    marginTop: '-4px',
                                }}
                            >
                                <Box padding={2} paddingTop={1} paddingBottom={1}>
                                    {domain}
                                </Box>
                            </Typography>
                        </Stack>

                        <Stack direction={'row'} spacing={2} justifyContent={'center'} paddingTop={2}>
                            <Button onClick={onClickPost} variant="contained" style={{ fontWeight: '500' }}>
                                <img
                                    src={LinkedinIcon}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                    }}
                                    alt="linkedin"
                                />
                                {t('certificate.share.linkedin.post.button')}
                            </Button>
                        </Stack>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ alignSelf: 'center', marginBottom: '16px' }}></DialogActions>
            </Dialog>
        </>
    );
}
